import {
	useSuspenseInfiniteQuery,
	useSuspenseQuery,
} from "@tanstack/react-query";
import { useParams } from "@tanstack/react-router";
import { Suspense } from "react";
import { Heading } from "@/components/catalyst/Heading";
import { orpc } from "@/orpc/client";
import { ActivityChartSection } from "./components/ActivityChartSection";
import { StatisticsCards } from "./components/StatisticsCards";
import { SubscribersTable } from "./components/SubscribersTable";

export function ReferralLinkDataPage() {
	const { linkId } = useParams({
		from: "/_app/apps/$appId/links/$linkId/data",
	});

	// Fetch statistics
	const { data: statistics } = useSuspenseQuery(
		orpc.referralLinks.statistics.queryOptions({ input: { id: linkId } }),
	);

	// Fetch subscribers with infinite scroll
	const {
		data: subscribersData,
		fetchNextPage,
		hasNextPage,
		isFetchingNextPage,
	} = useSuspenseInfiniteQuery(
		orpc.referralLinks.subscribers.infiniteOptions({
			input: (pageParam) => ({
				referralLinkId: linkId,
				limit: 20,
				cursor: pageParam as string | undefined,
			}),
			initialPageParam: undefined as string | undefined,
			getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
		}),
	);

	return (
		<div>
			<div className="flex items-center justify-between">
				<Heading level={2}>Referral Link Analytics</Heading>
			</div>

			{/* Statistics Cards */}
			<div className="mt-6">
				<StatisticsCards statistics={statistics} />
			</div>

			{/* Defer/Claim Chart - isolated in its own Suspense boundary */}
			<Suspense
				fallback={
					<div className="mt-8">
						<div className="flex items-center justify-between">
							<div>
								<h3 className="text-lg font-semibold text-base-content">
									Activity
								</h3>
								<p className="mt-1 text-sm text-base-content/60">
									Defers and claims over time
								</p>
							</div>
							<div className="h-8 w-32 animate-pulse rounded-lg bg-base-200" />
						</div>
						<div className="mt-4 h-75 animate-pulse rounded-xl border border-base-content/10 bg-base-100" />
					</div>
				}
			>
				<ActivityChartSection linkId={linkId} />
			</Suspense>

			{/* Subscribers Section */}
			<div className="mt-12">
				<h3 className="text-lg font-semibold text-base-content">Subscribers</h3>
				<p className="mt-1 text-sm text-base-content/60">
					Installations attributed to this referral link
				</p>

				<SubscribersTable
					data={subscribersData}
					hasNextPage={hasNextPage}
					isFetchingNextPage={isFetchingNextPage}
					fetchNextPage={fetchNextPage}
				/>
			</div>
		</div>
	);
}
